/**
 * ACL keys. Never inline a permission string in a component — import from here.
 *
 * Client-side checks are UX only: they hide nav, buttons, columns and filters.
 * The backend is the enforcement point. A hidden control is not a secured one.
 *
 * FORMAT: `<module>.<action>` with a SINGULAR module name — the backend's own
 * vocabulary. Confirmed for customers (`customer.view`, `customer.create`,
 * `customer.edit`, `customer.delete`), and a login response showed the same
 * shape for `payment.*`, `transaction.*`, `commission.*`, `report.*`,
 * `comparison-report.*` and `secure-link.*`. The modules not yet built follow
 * the same convention but are NOT confirmed — see customers/agent.md §23.
 */

/** One entry per module in the portal. Values are the API's singular names. */
export const MODULES = {
  dashboard: 'dashboard',
  customers: 'customer',
  events: 'event',
  hotels: 'hotel',
  groups: 'group',
  rooms: 'room',
  reservations: 'reservation',
  waitlist: 'waitlist',
  transactions: 'transaction',
  commission: 'commission',
  /**
   * Secure Hotel Portal — batches of reservations sent to a hotel over a
   * revocable secure link. The backend grants `view`, `create`, `delete` and
   * `*` only (no `edit`): Resend is a `create`, Revoke a `delete`
   * (user decision, 2026-10-29 — `src/screens/secure-links/agent.md` §23.3).
   */
  secureLinks: 'secure-link',
  reports: 'report',
  settings: 'setting',
  cms: 'cms',
  users: 'user',
  /** Roles & Permissions — its own backend module, not a sub-grant of `user`. */
  roles: 'role',
  partners: 'partner',
  audit: 'audit',
} as const

export type ModuleKey = (typeof MODULES)[keyof typeof MODULES]

/** The four CRUD actions every module supports. */
export const ACTIONS = {
  view: 'view',
  create: 'create',
  edit: 'edit',
  delete: 'delete',
} as const

export type ActionKey = (typeof ACTIONS)[keyof typeof ACTIONS]

/** `module.action`, e.g. 'customer.create'. */
export type ModulePermission = `${ModuleKey}.${ActionKey}`

/**
 * Sensitive actions granted individually, outside the CRUD matrix.
 * Each one gates a specific capability the client asked to restrict.
 */
export const SENSITIVE = {
  /** Superadmin grants this to specific senior staff only. */
  editPastReservations: 'reservation.edit-past',
  /** Set the hotel confirmation number (APending → Active). */
  confirmReservation: 'reservation.confirm',
  /** Cancel a reservation and (re)send its cancellation email. */
  cancelReservation: 'reservation.cancel',
  /** Opens the "Make it Large Event" configuration modal. */
  configureLargeEvent: 'event.configure-large-event',
  /** Shows the Commission Pending filter on the reservations listing. */
  viewCommissionPending: 'reservation.filter-commission-pending',
  /** Shows the Fees Pending filter on the reservations listing. */
  viewFeesPending: 'reservation.filter-fees-pending',
  /** Settle commission (Pay One / Pay All). */
  settleCommission: 'commission.settle',
} as const

export type SensitivePermission = (typeof SENSITIVE)[keyof typeof SENSITIVE]

export type Permission = ModulePermission | SensitivePermission

/** Builds a module permission key. */
export function permission(module: ModuleKey, action: ActionKey): ModulePermission {
  return `${module}.${action}`
}

/** Roles are data (created and edited in Users & Roles). This is the one fixed role. */
export const SUPERADMIN_ROLE = 'superadmin'

/**
 * Backend module names mapped onto ours, taken from the live login payload
 * (2026-09-02): `payment.view`, `transaction.view`, `wait-list.*`, `booking.*`,
 * `comparison-report.view`, … Modules with no alias here (`commission`, `cms`,
 * `dashboard`, `secure-link`) pass through under their own name.
 *
 * `role` is NOT an alias of `user`: the backend grants `role.view`,
 * `role.create`, `role.edit` and `role.delete` as their own module (confirmed
 * from a login payload, 2026-09-17), which is what gates Roles & Permissions.
 */
const API_MODULE_ALIASES: Readonly<Record<string, ModuleKey>> = {
  payment: MODULES.transactions,
  transaction: MODULES.transactions,
  customer: MODULES.customers,
  event: MODULES.events,
  hotel: MODULES.hotels,
  group: MODULES.groups,
  room: MODULES.rooms,
  booking: MODULES.reservations,
  reservation: MODULES.reservations,
  'wait-list': MODULES.waitlist,
  report: MODULES.reports,
  'comparison-report': MODULES.reports,
  user: MODULES.users,
  role: MODULES.roles,
  partner: MODULES.partners,
  setting: MODULES.settings,
  'audit-log': MODULES.audit,
  'audit-trail': MODULES.audit,
}

/**
 * Translates one granted key from the API's vocabulary into ours:
 * `payment.view` → `transaction.view`, `wait-list.*` → `waitlist.*`,
 * `reservation.edit.past` → `reservation.edit-past`. A key with no dot is
 * returned unchanged.
 *
 * The result uses the **same `module.action` form every check builds**
 * (`permission()`, `SENSITIVE`). It used to join with `:`, which no check ever
 * asks for, so an aliased grant never matched: a role holding `wait-list.*`
 * was sent to /403 on Waitlist (found 2026-09-28 with the sales account).
 *
 * A `module.*` wildcard grants every action on that module — `usePermission`
 * honours it. `gateway/modules/auth.ts` stores BOTH the raw and the normalised
 * key, so checks keep working whichever vocabulary the backend settles on.
 */
export function normalizeApiPermission(key: string): string {
  const [module, ...rest] = key.split('.')
  if (!module || rest.length === 0) return key
  const mapped = API_MODULE_ALIASES[module] ?? module
  return `${mapped}.${rest.join('-')}`
}

/**
 * The module-wide key that would cover the given permission, keeping the
 * separator the key itself uses: `role.view` → `role.*`, `users:view` →
 * `users:*`. The session holds both vocabularies (see `normalizeApiPermission`),
 * so the wildcard must be built in whichever one the check was written in.
 */
export function wildcardOf(key: string): string {
  const separator = key.includes('.') ? '.' : ':'
  return `${key.split(separator)[0]}${separator}*`
}
